// https://github.com/vercel/next.js/blob/8fdb93d76f9692f5f3188259ac643e1d2e960736/examples/api-routes-apollo-server-and-client/apollo/client.js
import React from 'react';
// eslint-disable-next-line import/named
import {
  ApolloClient,
  ApolloLink,
  defaultDataIdFromObject,
  from,
  gql,
  HttpLink,
  InMemoryCache,
  NormalizedCacheObject,
} from '@apollo/client';
// eslint-disable-next-line import/named
import { ErrorResponse, onError } from '@apollo/client/link/error';
import { SentryLink } from 'apollo-link-sentry';
import * as Sentry from '@sentry/nextjs';
import { GraphQLError } from 'graphql';
import { GetStaticPropsContext, GetServerSidePropsContext } from 'next';
import { Session } from 'next-auth';
import { getSession, useSession } from 'next-auth/react';

export type ApolloClientType = ApolloClient<NormalizedCacheObject>;
export type SessionType = Session | null | undefined;

type GetStaticApolloClient = (context?: GetStaticPropsContext | GetServerSidePropsContext) => Promise<{
  apolloClient: ApolloClientType;
  session: SessionType;
}>;
let apolloClient: ApolloClientType;
let _accessToken: string | undefined;
let hasToken = false;
let transactionId: string;

const GRAPHQL_URI = process.env.NEXT_PUBLIC_API_URL;

const isGraphQLErrorCategoryIgnored = ({ message, extensions }: GraphQLError): boolean => {
  const is403Error = message === 'This action is unauthorized.';
  const isAuthenticationError = extensions?.category === 'authentication';
  const isGraphQLValidationError = extensions?.category === 'validation';
  const isBillingError = extensions?.category === 'billing';

  return is403Error || isAuthenticationError || isGraphQLValidationError || isBillingError;
};

export const isServerError = (error: ErrorResponse): boolean =>
  Boolean(error.networkError || error.graphQLErrors?.some(({ extensions }) => extensions?.category === 'internal'));

const errorLink = onError((error) => {
  // const { showErrorNotification } = useNotification();
  //
  // if (isServerError(error))
  //   showErrorNotification({
  //     title: '¡Ups!',
  //     message: 'Se ha producido un error inesperado. Por favor, inténtalo otra vez o contacta con nosotros.',
  //   });

  if (error.graphQLErrors)
    error.graphQLErrors.forEach((graphqlError) => {
      if (!isGraphQLErrorCategoryIgnored(graphqlError)) Sentry.captureMessage(graphqlError.message);
    });
  if (error.networkError) Sentry.captureException(error.networkError);
});

const authMiddleware = () =>
  new ApolloLink((operation, forward) => {
    operation.setContext(({ headers = {} }) => ({
      headers: {
        ...headers,
        ...(_accessToken && { Authorization: `Bearer ${_accessToken}` }),
      },
    }));

    return forward(operation);
  });

export const createApolloClient = (): ApolloClientType =>
  new ApolloClient({
    cache: new InMemoryCache({
      dataIdFromObject: (object) => {
        switch (object.__typename) {
          case 'Query':
            return 'ROOT_QUERY';
          default:
            return defaultDataIdFromObject(object);
        }
      },
    }),
    ssrMode: typeof window === 'undefined',
    link: from([
      authMiddleware(),
      errorLink,
      new SentryLink({ attachBreadcrumbs: { includeError: true, includeFetchResult: true } }),
      new HttpLink({
        uri: `${GRAPHQL_URI}/graphql`,
        credentials: process.env.NODE_ENV === 'production' ? 'same-origin' : 'include',
      }),
    ]),
  });

export const initializeApollo = (accessToken?: string, initialState = {}): ApolloClientType => {
  let _apolloClient = apolloClient;

  if (!_apolloClient || (!hasToken && accessToken)) {
    _apolloClient = createApolloClient();
  }

  _accessToken = accessToken;
  hasToken = Boolean(accessToken);

  // If your page has Next.js data fetching methods that use Apollo Client,
  // the initial state gets hydrated here
  if (initialState) {
    _apolloClient.cache.restore(initialState);
  }

  // For SSG and SSR always create a new Apollo Client
  if (typeof window === 'undefined') return _apolloClient;
  // Create the Apollo Client once in the client
  if (!apolloClient) apolloClient = _apolloClient;

  return _apolloClient;
};

export const getStaticApolloClient: GetStaticApolloClient = async (context) => {
  const session: SessionType = context ? await getSession(context as GetServerSidePropsContext) : null;
  // @ts-expect-error fix typings
  const apolloClient = initializeApollo(session?.accessToken as string | undefined);

  return { apolloClient, session };
};

export const useApollo = (initialState: NormalizedCacheObject): ApolloClientType => {
  const { data: session } = useSession();

  // @ts-expect-error fix typings
  const accessToken = session?.accessToken as string | undefined;
  transactionId = Math.random().toString(36).substring(2, 9);

  if (typeof window !== 'undefined') (window as any).accessToken = accessToken;
  if (_accessToken !== accessToken) _accessToken = accessToken;

  Sentry.configureScope((scope) => {
    if (session?.user?.email) scope.setUser({ email: session.user.email });
    scope.setTag('transaction_id', transactionId);
  });

  return React.useMemo(() => initializeApollo(accessToken, initialState), [accessToken]);
};
